import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HttpError, type CardId } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { boardRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext, useSnackbar } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';

export interface UseCloseCard {
  /** Open the confirmation dialog. */
  requestClose: () => void;
  /** Confirm — dispatches `card.close` and closes the dialog on success. */
  confirmClose: () => void;
  /** Dismiss the dialog without closing. */
  cancel: () => void;
  isOpen: boolean;
  pending: boolean;
  reason: string;
  setReason: (value: string) => void;
}

/**
 * "Concluir" flow for a card. Owns the confirm-dialog state and optional
 * reason, dispatching `card.close` (outcome `resolved`). Mirrors
 * `useClaimCard`; surfaces failures via snackbar since the dispatch is
 * ungated client-side and a 403 from authz must be visible.
 */
export function useCloseCard(cardId: CardId): UseCloseCard {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      const trimmed = reason.trim();
      return boardRepository.dispatchCardEvent(orgId, cardId, {
        event: {
          type: 'card.close',
          outcome: 'resolved',
          ...(trimmed ? { reason: trimmed } : {}),
        },
      });
    },
    onSuccess: () => {
      setIsOpen(false);
      setReason('');
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(orgId, cardId) }),
      );
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.boards.all }));
      // The overview ("Você está atendendo") is a separate read model; without
      // this the closed card stays on the home page until a hard reload.
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.overview.all }));
    },
    onError: (err: unknown) => {
      showSnackbar({
        severity: 'error',
        message:
          err instanceof HttpError || err instanceof Error
            ? err.message
            : t('board.drawer.close.error'),
      });
    },
  });

  return {
    requestClose: () => {
      setIsOpen(true);
    },
    confirmClose: () => {
      mutation.mutate();
    },
    cancel: () => {
      setIsOpen(false);
    },
    isOpen,
    pending: mutation.isPending,
    reason,
    setReason,
  };
}
